import React, { useContext, useEffect, useState } from 'react';
import BigNumber from 'bignumber.js';
import { faCog, faStopwatch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NftNav from './components/NftNav';
import NftCard from './components/NftCard';
import NftBuyModal from './NftBuyModal';
import NftInfo from './components/NftInfo';

import { WalletContext } from '../../context/wallet';
import { BlockContext } from '../../context/block';
import { ToastContext } from '../../context/toast';

import { fetchNftSale, approveNftSale } from '../../blockchain/nft';

const NftSalePage = () => {
  const { walletAddress, handleConnectClick } = useContext(WalletContext);
  const { currentBlock } = useContext(BlockContext);
  const { addToast } = useContext(ToastContext);

  const [pendingTx, setPendingTx] = useState(0);
  const [startDiff, setStartDiff] = useState(0);
  const [nftSaleState, setNftSaleState] = useState({
    loading: false,
    firstLoad: true,
    nftCardsRemaining: 0,
    userNftBalance: 0,
    tokenDecimals: 0,
    userBalance: 0,
    userAllowance: 0,
    startBlock: 0,
    salePrice: 0,
    nftCardPerAccountMaxTotal: 0,
    userNftCardTotally: 0,
  });
  const [isNftBuyModalActive, setIsNftBuyModalActive] = useState(false);

  const handleNftBuyModalClose = () => setIsNftBuyModalActive(false);
  const handleNftBuyModalOpen = () => setIsNftBuyModalActive(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const syncNftSale = async () => {
      setNftSaleState(prevState => ({ ...prevState, loading: true }));
      const nftSaleResult = await fetchNftSale();
      setNftSaleState(prevState => ({ ...prevState, ...nftSaleResult, loading: false }));
    }

    const intervalId = setInterval(syncNftSale, 5000);

    syncNftSale();

    return () => clearInterval(intervalId);
  }, [setNftSaleState]);

  useEffect(() => {
    if (currentBlock) {
      setStartDiff(new BigNumber(nftSaleState.startBlock).minus(currentBlock).toNumber());
    }
  }, [currentBlock, nftSaleState, setStartDiff]);

  const handleApprove = async () => {
    setPendingTx(true);
    let tx;
    try {
      tx = await approveNftSale('usdt');
      await tx.wait();
      addToast('NFT Sale token approve succeeded', 'is-success');
    } catch (error) {
      tx = { error: error.message };
    }

    if(tx?.error !== undefined) {
      console.log('error', tx.error);
      addToast('NFT Sale token approve failed', 'is-danger');
    }

    setPendingTx(false);
  }

  const renderUnlockApproveOrBuyButton = () => {
    if (walletAddress === null) {
      return (
        <button className="button is-primary is-fullwidth" onClick={ handleConnectClick }>
          Unlock
        </button>
      );
    }

    if (!isLoading() && new BigNumber(nftSaleState.nftCardsRemaining).eq(0)) {
      return (
        <button type="button" disabled className="button is-primary is-fullwidth">SOLD OUT</button>
      );
    }

    if (new BigNumber(nftSaleState.userAllowance).eq(0)) {
      return (
        <button
          type="button"
          disabled={ pendingTx || isLoading() || startDiff > 0 }
          className={ `button is-primary is-fullwidth ${pendingTx ? 'is-loading' : ''}` }
          onClick={ handleApprove }
        >
          APPROVE USDT.e
        </button>
      );
    }

    return (
      <button
        type="button"
        disabled={ isLoading() || startDiff > 0 }
        className="button is-primary is-fullwidth"
        onClick={ () => handleNftBuyModalOpen() }
      >
        Buy
      </button>
    );
  }

  const isLoading = () => nftSaleState.firstLoad && nftSaleState.loading;

  return (
    <>
      <NftNav />
      <header className="hero parallax" style={{ backgroundImage: 'url("/images/parallax/bg-1.png")' }}>
        <div className="hero-body">
          <div className="container">
            <div className="hero-box has-text-centered">
              <p className="title">NFT ArtWorks Sale</p>
              <p className="subtitle"><span className="has-text-primary">NFT's ArtWorks</span> power will be unique and generated randomly.</p>
            </div>
          </div>
        </div>
      </header>
      <main role="main" className="section">
        <div className="container">
          <div className="columns is-multiline is-justify-content-center">
            <div className="column is-half-desktop">
              <article className="message">
                <div className="message-body">
                  <p className="title">NFT Public Sale</p>
                  <div className="level is-mobile mb-1">
                    <div className="level-left">
                      <div className="level-item">
                        NFTs Remaining:
                      </div>
                    </div>
                    <div className="level-left">
                      <div className="level-item">
                        <h6 className="has-text-weight-semibold has-text-primary is-size-6">
                          { isLoading() ? <FontAwesomeIcon icon={ faCog } spin /> : (new BigNumber(nftSaleState.nftCardsRemaining).eq(0) ? 'SOLD OUT' : new BigNumber(nftSaleState.nftCardsRemaining).toFormat(0)) }
                        </h6>
                      </div>
                    </div>
                  </div>
                  <div className="level is-mobile mb-1">
                    <div className="level-left">
                      <div className="level-item">
                        Your NFT balance:
                      </div>
                    </div>
                    <div className="level-left">
                      <div className="level-item">
                        <h6 className="has-text-weight-semibold has-text-primary is-size-6">{ isLoading() ? <FontAwesomeIcon icon={ faCog } spin /> : new BigNumber(nftSaleState.userNftBalance).toFormat(0) }</h6>
                      </div>
                    </div>
                  </div>
                  <div className="level is-mobile mb-1">
                    <div className="level-left">
                      <div className="level-item">
                        Your USDT.e balance:
                      </div>
                    </div>
                    <div className="level-left">
                      <div className="level-item">
                        <h6 className="has-text-weight-semibold has-text-primary is-size-6">{ isLoading() ? <FontAwesomeIcon icon={ faCog } spin /> : new BigNumber(nftSaleState.userBalance).div(new BigNumber(10).pow(nftSaleState.tokenDecimals)).toFormat(3) }</h6>
                      </div>
                    </div>
                  </div>
                  <div className="level is-mobile">
                    <div className="level-left">
                      <div className="level-item">
                        You can buy up to:
                      </div>
                    </div>
                    <div className="level-left">
                      <div className="level-item">
                        <h6 className="has-text-weight-semibold has-text-primary is-size-6">{ isLoading() ? <FontAwesomeIcon icon={ faCog } spin /> : new BigNumber(nftSaleState.nftCardPerAccountMaxTotal).toFormat(0) } NFTs</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
              <div className="card mb-3">
                <div className="card-header">
                  <p className="card-header-title">
                    BUY NFT with USDT.e, only <strong className="has-text-primary ml-1">${ new BigNumber(nftSaleState.salePrice).div(new BigNumber(10).pow(nftSaleState.tokenDecimals)).toFormat(0) }</strong>
                  </p>
                </div>
                <div className="card-content">
                  { renderUnlockApproveOrBuyButton() }
                </div>
                {startDiff > 0 ? (
                  <div className="card-footer">
                    <a href={`${process.env.REACT_APP_EXPLORER}/block/countdown/${nftSaleState.startBlock}`} target="_blank" rel="noreferrer" className="card-footer-item">
                      <span className="icon-text">
                        <span className="icon">
                          <FontAwesomeIcon icon={ faStopwatch } />
                        </span>
                        Blocks remaining to start { startDiff }
                      </span>
                    </a>
                  </div>
                ) : null}
              </div>
              <NftInfo />
            </div>
            <div className="column is-half-desktop">
              <NftCard nftData={{
                pid: 2110,
                experience: 287,
                generation: 0,
                power: 27,
              }} />
            </div>
          </div>
        </div>
      </main>
      <div className="parallax is-hidden-mobile" style={{ backgroundImage: 'url("/images/parallax/bg-1.png")', height: '100vh' }} />
      <NftBuyModal
        nftSaleState={ nftSaleState }
        isNftBuyModalActive={ isNftBuyModalActive }
        onNftBuyModalClose={ handleNftBuyModalClose }
      />
    </>
  );
}

export default NftSalePage;
